import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from '@tanstack/react-router';
import { installShell } from '@selflevel/ui';
import { router } from './router';
import { db } from './store/db';
import { getSettings, initSession } from './store/session';
import * as mutaciones from './store/mutations';
import './styles/app.css';

/**
 * Lo primero, antes de pintar: tema, modo sólido en equipos flojos y congelado
 * del cristal durante el scroll. Si esto corriera después del primer render se
 * vería el salto de tema, que es la señal más clara de app barata.
 */
installShell();

/**
 * Persistencia del almacén local. En iOS no protege de verdad —hay PWA que
 * pierden IndexedDB— y la respuesta real a eso es Capacitor. Se pide igual: en
 * Android y escritorio sí funciona y no cuesta nada.
 */
void navigator.storage?.persist?.().catch(() => undefined);

/**
 * UNA PANTALLA QUE YA NO EXISTE EN EL SERVIDOR. Tras un despliegue, un móvil que
 * sigue con la versión anterior en memoria pide el archivo de una pantalla que se
 * carga aparte (Diario, Semana, Ajustes…) con un nombre que ya no está, y la pantalla
 * no abre. Vite avisa con `vite:preloadError`: se recarga una vez para entrar con la
 * versión nueva. Una vez por minuto como mucho, para no entrar en bucle si el fallo
 * es otro (sin red, por ejemplo).
 */
window.addEventListener('vite:preloadError', (evento) => {
  const CLAVE = 'selflevel.recargaPorVersion';
  try {
    const ultima = Number(sessionStorage.getItem(CLAVE) ?? 0);
    if (Date.now() - ultima < 60_000) return;
    sessionStorage.setItem(CLAVE, String(Date.now()));
  } catch {
    return;
  }
  evento.preventDefault();
  window.location.reload();
});

/**
 * Sin pellizco. El iPhone ignora `user-scalable=no` del viewport y deja ampliar
 * con dos dedos; ampliada, la app se queda descolocada y hay que deshacerlo a
 * mano. Sus eventos `gesture*` solo existen en WebKit: en lo demás esto no hace nada.
 */
for (const gesto of ['gesturestart', 'gesturechange'] as const) {
  document.addEventListener(gesto, (e) => e.preventDefault(), { passive: false });
}

const container = document.getElementById('root');
if (!container) throw new Error('falta #root');

/**
 * La sesión local se abre ANTES del primer render. Sin `deviceId` y `userId` no
 * se pueden calcular los identificadores deterministas, y sin ellos una
 * escritura temprana crearía una fila con un id que luego no cuadraría.
 */
void (async () => {
  await initSession();
  await getSettings();

  /**
   * Solo en desarrollo: el almacén y las mutaciones colgando de `window`.
   * Sirve para sembrar datos de un año y mirar el rewind sin esperar doce
   * meses, y para inspeccionar IndexedDB desde la consola. `import.meta.env.DEV`
   * lo elimina del bundle de producción — no queda ni la referencia.
   */
  if (import.meta.env.DEV) {
    Object.assign(window, { selflevel: { db, ...mutaciones } });
  }

  createRoot(container).render(
    <StrictMode>
      <RouterProvider router={router} />
    </StrictMode>,
  );

  /**
   * La sincronización arranca DESPUÉS del primer render, y a propósito.
   *
   * Es lo único de la app que depende de la red, y dejarla por delante metería una
   * petición HTTP en el camino crítico de la primera pantalla. El usuario sin
   * cuenta —que son todos el primer día— no debe pagar ni un milisegundo por una
   * función que no usa.
   *
   * Si hay sesión, `reanudarSesion` vincula y arranca el motor sola.
   */
  void import('./store/cuenta').then(({ reanudarSesion }) => reanudarSesion());

  /**
   * Los recordatorios los programa el dispositivo, nunca el servidor (PRD
   * 13.3). Se VIGILAN, no se programan una vez: una vez al arrancar dejaba
   * fuera todo lo que el usuario cambiara después —marcar el hábito antes de
   * su hora, poner uno nuevo, pasar la noche con la app abierta—.
   */
  /**
   * El service worker. Tampoco en el camino crítico: registrarlo antes del
   * primer render le haría descargar el precache entero mientras el usuario
   * espera a ver su pantalla. Ver `store/actualizacion.ts` para por qué esta
   * línea es nueva —nadie lo registraba— y lo que eso rompía.
   */
  void import('./store/actualizacion').then(({ registrarServiceWorker }) =>
    registrarServiceWorker(),
  );

  void import('./store/recordatorios').then(({ vigilarRecordatorios }) => vigilarRecordatorios());
})();
